import {
  ALL_ITEMS,
  DNS_PRESETS,
  GROUPS,
  ITEM_BY_ID,
  MIRRORS,
  REGIONS,
  type DnsPreset,
  type MirrorKey,
  type RegionKey,
  type RuleItem,
} from './catalog.ts';

export const MAX_UPSTREAMS = 10;

/**
 * 出口:proxy=🚀 节点选择,auto=♻️ 自动选择,地区组,或 air:N=第 N 个机场的分组。
 * 地区组 regionType=both 时分为手动(hk)与自动(hk:auto)两个组;其他模式下两者指向同一个地区组
 */
export type Outbound = 'proxy' | 'auto' | 'direct' | 'reject' | RegionKey | `${RegionKey}:auto` | `air:${number}`;

export type RegionType = 'url-test' | 'select' | 'both';

/** 规则去向:cat=所属分类策略组,own=该规则的独立策略组,或直接指向某个出口 */
export type RuleTarget = 'cat' | 'own' | Outbound;

export type SourceMode = 'auto' | 'inline' | 'provider';

export interface Upstream {
  name: string;
  url: string;
}

export interface UserConfig {
  v: 2;
  name: string;
  upstreams: Upstream[];
  ua: string;
  mode: SourceMode;
  mirror: MirrorKey;
  rules: string[];
  /** 仅保存与默认值不同的规则去向 */
  targets: Record<string, RuleTarget>;
  /** 用户调整过的规则顺序(规则 id 与 custom:*);空数组表示推荐顺序 */
  order: string[];
  /** 服务策略组的默认出口,key 为分类组 key 或 own:<规则 id> */
  groupDefaults: Record<string, Outbound>;
  regions: RegionKey[];
  regionType: RegionType;
  /** 服务策略组在分组选项之后平铺全部节点(适合节点少、没有地区概念的机场) */
  flatNodes: boolean;
  final: 'proxy' | 'direct';
  dns: DnsPreset;
  ipv6: boolean;
  allowLan: boolean;
  sniffer: boolean;
  customDirect: string[];
  customProxy: string[];
  customReject: string[];
}

export const DEFAULT_UA = 'clash.meta';

export function defaultConfig(): UserConfig {
  return {
    v: 2,
    name: '我的订阅',
    upstreams: [{ name: '机场1', url: '' }],
    ua: DEFAULT_UA,
    mode: 'auto',
    mirror: 'fastly',
    rules: ALL_ITEMS.filter((i) => i.defaultOn).map((i) => i.id),
    targets: {},
    order: [],
    groupDefaults: {},
    regions: ['hk', 'tw', 'jp', 'sg', 'us'],
    regionType: 'url-test',
    flatNodes: false,
    final: 'proxy',
    dns: 'fakeip-safe',
    ipv6: false,
    allowLan: false,
    sniffer: true,
    customDirect: [],
    customProxy: [],
    customReject: [],
  };
}

/** 规则的推荐去向:有分类组的走分类组,「海外常用」这类直接走 🚀 节点选择 */
export function defaultTarget(item: RuleItem): RuleTarget {
  return item.group === 'proxy' ? 'proxy' : 'cat';
}

export function targetOf(cfg: UserConfig, item: RuleItem): RuleTarget {
  return cfg.targets[item.id] ?? defaultTarget(item);
}

/** 兼容 v1(单个 upstream 字段)的旧数据 */
export function migrateConfig(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const o = raw as Record<string, unknown>;
  if (o.upstreams === undefined && typeof o.upstream === 'string') {
    const { upstream, ...rest } = o;
    return { ...rest, v: 2, upstreams: [{ name: '机场1', url: upstream }], targets: rest.targets ?? {} };
  }
  return raw;
}

export class ValidationError extends Error {}

const DOMAIN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,62}$/;
// 机场名会出现在策略组名和节点名前缀里,排除控制字符和配置语法里有特殊含义的字符
const UPSTREAM_NAME_RE = /^[^\u0000-\u001f\u007f"\\,|#:]{1,16}$/;
const BASE_OUTBOUNDS = ['proxy', 'auto', 'direct', 'reject', ...REGIONS.flatMap((r) => [r.key, `${r.key}:auto`])];
const GROUP_KEYS = new Set<string>(GROUPS.map((g) => g.key));
const REGION_KEYS = new Set<string>(REGIONS.map((r) => r.key));
const MIRROR_KEYS = new Set<string>(MIRRORS.map((m) => m.key));
const DNS_KEYS = new Set<string>(DNS_PRESETS.map((d) => d.key));
const ITEM_IDS = new Set<string>(ITEM_BY_ID.keys());
const ORDER_IDS = new Set<string>(['custom:reject', 'custom:direct', 'custom:proxy', ...ITEM_IDS]);

function fail(msg: string): never {
  throw new ValidationError(msg);
}

function str(v: unknown, field: string, max: number): string {
  if (typeof v !== 'string') fail(`${field} 必须是字符串`);
  const s = v.trim();
  if (s.length > max) fail(`${field} 过长`);
  return s;
}

function bool(v: unknown, field: string): boolean {
  if (typeof v !== 'boolean') fail(`${field} 必须是布尔值`);
  return v;
}

function oneOf<T extends string>(v: unknown, allowed: Set<string>, field: string): T {
  if (typeof v !== 'string' || !allowed.has(v)) fail(`${field} 取值无效`);
  return v as T;
}

function arr(v: unknown, field: string, max: number): unknown[] {
  if (!Array.isArray(v)) fail(`${field} 必须是数组`);
  if (v.length > max) fail(`${field} 条目过多`);
  return v;
}

function record(v: unknown, field: string): Record<string, unknown> {
  if (v === undefined) return {};
  if (!v || typeof v !== 'object' || Array.isArray(v)) fail(`${field} 格式错误`);
  return v as Record<string, unknown>;
}

/** 仅允许公网 http(s) 地址,拒绝内网/本机地址 */
export function validateUpstream(raw: string): string {
  if (!raw) fail('请填写机场订阅地址');
  if (raw.length > 2048) fail('订阅地址过长');
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    fail(`订阅地址不是合法 URL:${raw.slice(0, 60)}`);
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') fail('订阅地址只支持 http/https');
  if (u.username || u.password) fail('订阅地址不能包含账号密码');
  const host = u.hostname.toLowerCase().replace(/^\[|\]$/g, '');
  if (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host.endsWith('.local') ||
    host.endsWith('.internal') ||
    /^(0|10|127)\./.test(host) ||
    /^169\.254\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
    /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(host) ||
    host === '::1' ||
    host === '::' ||
    /^f[cd][0-9a-f]{2}:/.test(host) ||
    /^fe80:/.test(host) ||
    (!host.includes('.') && !host.includes(':'))
  ) {
    fail('订阅地址不能指向内网或本机');
  }
  return u.toString();
}

function domains(v: unknown, field: string): string[] {
  const out = new Set<string>();
  for (const d of arr(v, field, 500)) {
    const s = str(d, field, 253).toLowerCase().replace(/^\+?\./, '');
    if (!s) continue;
    if (!DOMAIN_RE.test(s)) fail(`${field} 中的「${s.slice(0, 60)}」不是合法域名`);
    out.add(s);
  }
  return [...out];
}

export function validateUa(v: unknown): string {
  const ua = str(v ?? DEFAULT_UA, 'User-Agent', 200) || DEFAULT_UA;
  if (!/^[\x20-\x7e]+$/.test(ua)) fail('User-Agent 只能包含可见 ASCII 字符');
  return ua;
}

export function validateUpstreams(v: unknown): Upstream[] {
  const list = arr(v, '机场订阅', MAX_UPSTREAMS);
  if (!list.length) fail('请至少填写一个机场订阅');
  const names = new Set<string>();
  const urls = new Set<string>();
  return list.map((raw, i) => {
    const o = record(raw, '机场订阅');
    const name = str(o.name ?? '', '机场名称', 16) || `机场${i + 1}`;
    if (!UPSTREAM_NAME_RE.test(name)) fail(`机场名称「${name}」包含不支持的字符`);
    if (names.has(name)) fail(`机场名称「${name}」重复`);
    names.add(name);
    const url = validateUpstream(str(o.url ?? '', '订阅地址', 2048));
    if (urls.has(url)) fail('存在重复的订阅地址');
    urls.add(url);
    return { name, url };
  });
}

/** 严格校验来自客户端的配置,只保留已知字段 */
export function validateConfig(input: unknown): UserConfig {
  const migrated = migrateConfig(input);
  if (!migrated || typeof migrated !== 'object' || Array.isArray(migrated)) fail('配置格式错误');
  const o = migrated as Record<string, unknown>;

  const ups = validateUpstreams(o.upstreams);
  const outbounds = new Set<string>([...BASE_OUTBOUNDS, ...(ups.length > 1 ? ups.map((_, i) => `air:${i}`) : [])]);

  const name = str(o.name ?? '', '名称', 40) || '我的订阅';
  if (/[\u0000-\u001f\u007f"\\]/.test(name)) fail('名称包含非法字符');

  const ua = validateUa(o.ua);

  const rules = [...new Set(arr(o.rules, '规则', ALL_ITEMS.length).map((r) => oneOf<string>(r, ITEM_IDS, '规则')))];

  const targetValues = new Set<string>(['cat', 'own', ...outbounds]);
  const targets: Record<string, RuleTarget> = {};
  for (const [id, t] of Object.entries(record(o.targets, '规则去向'))) {
    const item = ITEM_BY_ID.get(oneOf<string>(id, ITEM_IDS, '规则去向'))!;
    const target = oneOf<RuleTarget>(t, targetValues, `「${item.label}」的去向`);
    // 「海外常用」类规则没有分类组,cat 等同于默认值
    if (target === 'cat' && item.group === 'proxy') continue;
    if (target !== defaultTarget(item)) targets[id] = target;
  }

  const groupDefaults: Record<string, Outbound> = {};
  for (const [k, val] of Object.entries(record(o.groupDefaults, '策略组设置'))) {
    const validKey = GROUP_KEYS.has(k) || (k.startsWith('own:') && ITEM_IDS.has(k.slice(4)));
    if (!validKey) fail('策略组取值无效');
    groupDefaults[k] = oneOf<Outbound>(val, outbounds, '策略组出口');
  }

  const regions = [...new Set(arr(o.regions, '地区', REGIONS.length).map((r) => oneOf<RegionKey>(r, REGION_KEYS, '地区')))];

  return {
    v: 2,
    name,
    upstreams: ups,
    ua,
    mode: oneOf<SourceMode>(o.mode ?? 'auto', new Set(['auto', 'inline', 'provider']), '节点模式'),
    mirror: oneOf<MirrorKey>(o.mirror, MIRROR_KEYS, '规则镜像'),
    rules,
    targets,
    order: [...new Set(arr(o.order ?? [], '规则顺序', ORDER_IDS.size).map((id) => oneOf<string>(id, ORDER_IDS, '规则顺序')))],
    groupDefaults,
    regions,
    regionType: oneOf<RegionType>(o.regionType, new Set(['url-test', 'select', 'both']), '地区组类型'),
    flatNodes: bool(o.flatNodes ?? false, '平铺节点'),
    final: oneOf(o.final, new Set(['proxy', 'direct']), '兜底策略'),
    dns: oneOf<DnsPreset>(o.dns, DNS_KEYS, 'DNS'),
    ipv6: bool(o.ipv6, 'IPv6'),
    allowLan: bool(o.allowLan, '局域网连接'),
    sniffer: bool(o.sniffer, '域名嗅探'),
    customDirect: domains(o.customDirect ?? [], '自定义直连'),
    customProxy: domains(o.customProxy ?? [], '自定义代理'),
    customReject: domains(o.customReject ?? [], '自定义拦截'),
  };
}
